/* eslint-disable @typescript-eslint/no-explicit-any */
"use client";
import React, { useEffect, useState } from "react";
import { apiPost } from "@/utils/apiClient";
import SectionHeading from "../others/SectionHeading";
import InformationButton from "@/components/ui/InformationButton";
import RiskVsReturnTable from "../others/RiskVsReturnTable";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";

type Props = { schemeCode: string; schemeCategoryId: string };

interface TableRow {
  label: string;
  return: number;
  risk: number;
}

type BubblePoint = {
  x: number;
  y: number;
  z: number;
  name: string;
};

export default function RiskVsReturnSection({ schemeCode, schemeCategoryId }: Props) {
  const [seriesData, setSeriesData] = useState<any[]>([]);
  const [tableData, setTableData] = useState<TableRow[]>([]);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        const res = await apiPost("riskRatioChart", {
          scheme_category_id: schemeCategoryId,
          scheme_code: schemeCode,
        });

        if (res?.status === 1 && Array.isArray(res.data)) {
          const formattedSeries = res.data.map((item: any, idx: number) => ({
            name: idx === 0 ? "Fund" : "Category Average",
            color: idx === 0 ? "#2f7ed8" : "#910000",
            type: "bubble" as const,
            data: [
              {
                x: item.risk,
                y: item.return,
                z: item.fund_size,
                name: item.name,
              } as BubblePoint,
            ],
          }));

          const formattedTable = res.data.map((item: any, idx: number) => ({
            label: idx === 0 ? "Fund" : "Category",
            return: item.return,
            risk: item.risk,
          }));

          setSeriesData(formattedSeries);
          setTableData(formattedTable);
        }
      } catch (error) {
        console.error("Error fetching chart data:", error);
      }
    };

    fetchChartData();
  }, [schemeCode, schemeCategoryId]);

  const hasSeries = Array.isArray(seriesData) && seriesData.length > 0;
  const hasTable = Array.isArray(tableData) && tableData.length > 0;

  useEffect(() => {
    let mounted = true;

    import("highcharts/highcharts-more").then((mod) => {
      const initModule = mod.default || mod;

      if (typeof initModule === "function") {
        initModule(Highcharts);
      }

      if ((Highcharts as any).seriesTypes?.bubble) {
        if (mounted) setReady(true);
      } else {
        console.error("Bubble module NOT registered");
      }
    });

    return () => {
      mounted = false;
    };
  }, []);
  if (!hasSeries && !hasTable) return null;

  if (!ready || !(Highcharts as any).seriesTypes?.bubble) return;

  const chartOptions: Highcharts.Options = {
    chart: {
      type: "bubble",
      plotBorderWidth: 1,
      backgroundColor: "transparent",
      height: 350,
      style: {
        fontFamily: "Inter, sans-serif",
        fontSize: "13px",
      },
    },
    title: { text: "" },
    legend: {
      layout: "horizontal",
      align: "center",
      verticalAlign: "bottom",
      itemStyle: {
        fontWeight: "400",
        fontFamily: "Inter, sans-serif",
        fontSize: "13px",
      },
    },
    xAxis: {
      title: { text: "Risk (%)", style: { fontSize: "13px" } },
      labels: { format: "{value}%", style: { fontSize: "11px" } },
      gridLineWidth: 1,
      startOnTick: true,
      endOnTick: true,
      minPadding: 0,
      maxPadding: 0,
    },
    yAxis: {
      title: { text: undefined },
      labels: { format: "{value}%", style: { fontSize: "11px" } },
      gridLineWidth: 1,
      startOnTick: true,
      endOnTick: true,
      minPadding: 0,
      maxPadding: 0,
    },
    tooltip: {
      useHTML: true,
      headerFormat: `<span class="text-2xs leading-6 font-semibold truncate">{point.name}</span><br>`,
      pointFormat: `<div class="flex flex-col gap-x-2 gap-y-1 text-xs">
                        <span class="whitespace-nowrap">Risk: {point.x}%</span>
                        <span class="whitespace-nowrap">Return: {point.y}%</span>
                        <span class="whitespace-nowrap">Fund Size: ₹{point.z} Cr.</span>
                      </div>`,
    },
    plotOptions: {
      bubble: { minSize: "15%", maxSize: "40%" },
      series: { dataLabels: { enabled: false } },
    },
    series: seriesData,
    credits: { enabled: false },
  };

  return (
    <>
      <section className="mb-8 xs:mb-12">
        <div className="flex items-center gap-3 mb-4">
          <SectionHeading heading="Risk Vs. Return" />

          <InformationButton keywords="risk-vs-returns" />
        </div>

        <div className="w-full max-w-4xl mx-auto bg-white p-6 sm:p-5 border border-[#E9E9EB] rounded-4xl flex flex-col sm:flex-row justify-between gap-10">
          {/* Chart */}
          <div className="flex-1 sm:max-w-[50%] p-0 sm:p-6">
            <HighchartsReact highcharts={Highcharts} options={chartOptions} />
          </div>

          {/* Table */}
          <RiskVsReturnTable data={tableData || []} />
        </div>
      </section>
    </>
  );
}
